/* WastageTrendChart — iter97.21
 *
 * 14-day rolling wastage trend visualised as a compact SVG line chart.
 * Lives on the admin Inventory dashboard, between the AlertsStrip and the
 * tab navigation. Drives focus to material defect spikes early so admin
 * can adjust SOPs (resin shelf-life, sheet supplier, etc.) before they
 * eat into margin.
 *
 * Data source: GET /api/admin/inventory/wastage-trend?days=14
 *  → { days, series: [{date, wastage_qty, actual_consumed, total_lines,
 *     wastage_lines, high_wastage_lines}], totals, wastage_rate_pct }
 *
 * SVG-only (no Recharts dep) so it stays fast + lean.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { TrendUp, TrendDown, Minus, ChartLineUp, Warning } from "@phosphor-icons/react";


export default function WastageTrendChart() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        api.get("/admin/inventory/wastage-trend?days=14")
            .then((r) => setData(r.data))
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, []);

    /* Derive a 7-day-prior vs 7-day-recent delta to surface direction. */
    const trend = useMemo(() => {
        if (!data?.series?.length) return null;
        const half = Math.floor(data.series.length / 2);
        const prior = data.series.slice(0, half);
        const recent = data.series.slice(half);
        const sum = (arr) => arr.reduce((a, b) => a + (b.wastage_qty || 0), 0);
        const pSum = sum(prior);
        const rSum = sum(recent);
        if (pSum === 0 && rSum === 0) return { dir: "flat", delta: 0 };
        if (pSum === 0) return { dir: "up", delta: 100 };
        const pct = Math.round(((rSum - pSum) / pSum) * 100);
        return { dir: pct > 5 ? "up" : pct < -5 ? "down" : "flat", delta: pct };
    }, [data]);

    if (loading) {
        return (
            <div className="rounded-2xl border border-border bg-card p-5 animate-pulse">
                <div className="h-4 w-32 bg-secondary rounded" />
                <div className="h-24 mt-4 bg-secondary/60 rounded" />
            </div>
        );
    }
    if (error || !data) return null;

    const series = data.series || [];
    const maxVal = Math.max(1, ...series.map((d) => d.wastage_qty || 0));
    const W = 600;
    const H = 100;
    const PADX = 12;
    const PADY = 14;
    const stepX = series.length > 1 ? (W - PADX * 2) / (series.length - 1) : 0;
    const yOf = (v) => H - PADY - ((v / maxVal) * (H - PADY * 2));

    const points = series.map((d, i) => ({
        x: PADX + i * stepX,
        y: yOf(d.wastage_qty || 0),
        d,
    }));
    const pathD = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");
    const areaD = `${pathD} L ${(PADX + (series.length - 1) * stepX).toFixed(1)} ${(H - PADY).toFixed(1)} L ${PADX} ${(H - PADY).toFixed(1)} Z`;

    const TrendIcon = trend?.dir === "up" ? TrendUp : trend?.dir === "down" ? TrendDown : Minus;
    const trendCls = trend?.dir === "up"
        ? "text-rose-700 bg-rose-50 border-rose-200"
        : trend?.dir === "down"
            ? "text-emerald-700 bg-emerald-50 border-emerald-200"
            : "text-muted-foreground bg-secondary/40 border-border";
    const trendLabel = trend?.dir === "up"
        ? `+${trend.delta}% vs prior 7 days`
        : trend?.dir === "down"
            ? `${trend.delta}% vs prior 7 days`
            : "Steady";

    return (
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="wastage-trend-card">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                <div>
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground inline-flex items-center gap-1.5">
                        <ChartLineUp size={11} weight="bold" /> Wastage trend · 14 days
                    </div>
                    <div className="flex items-baseline gap-2 mt-0.5">
                        <span className="font-display text-2xl font-bold tabular-nums" data-testid="wastage-trend-total">
                            {data.totals?.wastage_qty?.toFixed(1) || "0.0"}
                        </span>
                        <span className="text-xs text-muted-foreground">units wasted</span>
                        <span className="text-xs text-muted-foreground">·</span>
                        <span className="text-xs font-semibold tabular-nums" data-testid="wastage-trend-rate">
                            {data.wastage_rate_pct?.toFixed(1) || "0.0"}% of consumption
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {data.totals?.high_wastage_lines > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border bg-rose-50 text-rose-700 border-rose-200 text-[10px] font-bold tracking-wider uppercase">
                            <Warning size={10} weight="fill" /> {data.totals.high_wastage_lines} high-wastage event{data.totals.high_wastage_lines === 1 ? "" : "s"}
                        </span>
                    )}
                    {trend && (
                        <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-bold tracking-wider uppercase ${trendCls}`}
                            data-testid="wastage-trend-direction"
                        >
                            <TrendIcon size={11} weight="bold" /> {trendLabel}
                        </span>
                    )}
                </div>
            </div>

            {data.totals?.wastage_qty > 0 ? (
                <svg
                    viewBox={`0 0 ${W} ${H}`}
                    preserveAspectRatio="none"
                    className="w-full h-24"
                    data-testid="wastage-trend-svg"
                >
                    <defs>
                        <linearGradient id="wastageArea" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="rgb(244 63 94)" stopOpacity="0.18" />
                            <stop offset="100%" stopColor="rgb(244 63 94)" stopOpacity="0" />
                        </linearGradient>
                    </defs>
                    {/* Gridlines (4 horizontal) */}
                    {[0.25, 0.5, 0.75].map((f) => (
                        <line
                            key={f}
                            x1={PADX} x2={W - PADX}
                            y1={PADY + f * (H - PADY * 2)}
                            y2={PADY + f * (H - PADY * 2)}
                            stroke="currentColor"
                            strokeOpacity="0.06"
                            strokeDasharray="3 3"
                        />
                    ))}
                    {/* Area + line */}
                    <path d={areaD} fill="url(#wastageArea)" />
                    <path d={pathD} fill="none" stroke="rgb(244 63 94)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                    {/* Per-day dots — bigger when high_wastage_lines > 0 */}
                    {points.map((p, i) => {
                        const hot = (p.d.high_wastage_lines || 0) > 0;
                        return (
                            <circle
                                key={i}
                                cx={p.x}
                                cy={p.y}
                                r={hot ? 3.5 : 2}
                                fill={hot ? "rgb(244 63 94)" : "white"}
                                stroke="rgb(244 63 94)"
                                strokeWidth="1.5"
                            >
                                <title>{`${p.d.date} · ${(p.d.wastage_qty || 0).toFixed(1)} units wasted (${p.d.wastage_lines} line${p.d.wastage_lines === 1 ? "" : "s"})${hot ? " · high-wastage day" : ""}`}</title>
                            </circle>
                        );
                    })}
                </svg>
            ) : (
                <div className="h-24 rounded-lg bg-emerald-50/60 border border-emerald-200 flex items-center justify-center text-xs text-emerald-800 font-semibold" data-testid="wastage-trend-empty">
                    No wastage recorded in the last 14 days · clean run! 🎉
                </div>
            )}

            {/* X-axis labels (first / middle / last) */}
            {series.length > 0 && (
                <div className="flex justify-between mt-1 text-[10px] text-muted-foreground tabular-nums px-1">
                    <span>{shortDate(series[0].date)}</span>
                    <span>{shortDate(series[Math.floor(series.length / 2)].date)}</span>
                    <span>{shortDate(series[series.length - 1].date)}</span>
                </div>
            )}
        </div>
    );
}


function shortDate(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}
